import { ResourceService } from 'src/app/shared/resource-service'
import { environment } from 'src/environments/environment'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'

import { Report } from './report'
import { ReportSerializer } from './report.serializer'
import { combineLatest, Observable, of } from 'rxjs'
import { TokenResponse } from 'src/app/core/auth/interfaces'
import { bookingActions } from '../booking/booking.actions'
import {
  CondOperator,
  QuerySortOperator,
  RequestQueryBuilder
} from '@nestjsx/crud-request'
import { AppState } from '../../core/core.state'
import { Store } from '@ngrx/store'
import { ProductGroupsPurchaseFlow } from '../../shared/models'
import { productActions } from '../product/product.actions'
import { configOptionsSelector } from '../configuration-option/configuration-option.selectors'
import { ConfigOptionsKeys } from '../../shared/constants'
import { Booking } from '../booking/booking'
import { saleItemSelectors } from '../sale/sale-item/sale-item.selectors'
import { saleSelectors } from '../sale/sale.selectors'
import { catchError, map, take } from 'rxjs/operators'
import { Sale, StatusCode } from '../sale/sale'
import { participationSelectors } from '../booking/participation/participation.selectors'
import { bookingItemActions } from '../booking/booking-item/booking-item.actions'
import { saleItemActions } from '../sale/sale-item/sale-item.actions'
import { Participation } from '../booking/participation/participation'
import { SaleItem } from '../sale/sale-item/sale-item'
import { Dictionary } from '@ngrx/entity'
import moment from 'moment-mini'
import { tagActions } from '../tag/tag.actions'
import { Actions, ofType } from '@ngrx/effects'
import { participationActions } from '../booking/participation/participation.actions'

function filterCancelledSales(
  booking: Booking,
  participantEntities: Dictionary<Participation>,
  saleItemEntities: Dictionary<SaleItem>,
  saleEntities: Dictionary<Sale>
) {
  const firstParticipant = booking?.participations?.[0]
  const participant = participantEntities?.[firstParticipant]
  const saleItem = saleItemEntities?.[participant?.saleItemId]
  const sale = saleEntities?.[saleItem?.saleId]
  return sale?.statusCode !== StatusCode.CANCELLED
}

@Injectable({
  providedIn: 'root'
})
export class ReportService extends ResourceService<Report> {
  private productGroupsOptions$ = configOptionsSelector(
    this.store,
    ConfigOptionsKeys.PRODUCT_GROUPS_PURCHASE_FLOW_CARD
  )

  constructor(
    private actions$: Actions,
    private http: HttpClient,
    reportSerializer: ReportSerializer,
    private store: Store<AppState>
  ) {
    super(http, `${environment.backendApiUrl}/reports`, reportSerializer)
  }

  public createOneByType(report: Report): Observable<TokenResponse> {
    return this.http.post<any>(
      `${environment.backendApiUrl}/reports/${report?.reportType}`,
      report
    )
  }

  public fetchProductsBasedCategory(category: string): void {
    this.productGroupsOptions$.subscribe((option) => {
      if (!option) {
        console.warn('Web option PRODUCT_GROUPS_PURCHASE_FLOW_CARD missing')
        return null
      }

      const productGroupsPurchaseFlow: Partial<ProductGroupsPurchaseFlow> =
        JSON.parse(option)

      // activity ids

      const productIds = productGroupsPurchaseFlow[category].productIds

      this.store.dispatch(
        productActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { id: { [CondOperator.IN]: productIds } },
            join: [{ field: 'parentBookableProducts' }]
          })
        })
      )
    })
  }

  public getSelectedBookingsByActivity$(
    productIds: number[],
    bookings: Booking[]
  ): Observable<Booking[]> {
    return combineLatest([
      this.store.select(saleItemSelectors.selectEntities),
      this.store.select(saleSelectors.selectEntities),
      this.store.select(participationSelectors.selectEntities)
    ]).pipe(
      map(([saleItemEntities, saleEntities, participantEntities]) => {
        return (
          bookings
            .filter((b) => productIds.includes(Number(b.linkedProductId)))
            .filter((booking) =>
              filterCancelledSales(
                booking,
                participantEntities,
                saleItemEntities,
                saleEntities
              )
            )
            // sort bookings asc by start date
            .sort(
              (a, b) =>
                // @ts-ignore
                new Date(a?.startDate).getTime() -
                // @ts-ignore
                new Date(b?.startDate).getTime()
            )
        )
      })
    )
  }

  public getSelectedBookingsByResources$(
    productIds: number[],
    bookings: Booking[]
  ): Observable<Booking[]> {
    return combineLatest([
      this.store.select(saleItemSelectors.selectEntities),
      this.store.select(saleSelectors.selectEntities),
      this.store.select(participationSelectors.selectEntities)
    ]).pipe(
      map(([saleItemEntities, saleEntities, participantEntities]) => {
        return (
          bookings
            .filter((b) => productIds.includes(Number(b.productId)))
            .filter((booking) =>
              filterCancelledSales(
                booking,
                participantEntities,
                saleItemEntities,
                saleEntities
              )
            )
            // sort bookings asc by start date
            .sort(
              (a, b) =>
                // @ts-ignore
                new Date(a?.startDate).getTime() -
                // @ts-ignore
                new Date(b?.startDate).getTime()
            )
        )
      })
    )
  }

  public getSelectedBookingsByResourcesOrActivity$(
    productIds: number[],
    bookings: Booking[]
  ): Observable<Booking[]> {
    return combineLatest([
      this.getSelectedBookingsByActivity$(productIds, bookings),
      this.getSelectedBookingsByResources$(productIds, bookings)
    ]).pipe(
      map(([activityBookings, resourceBookings]) => {
        const mergedArray = activityBookings.concat(
          resourceBookings.filter(
            (resourceBooking) => activityBookings.indexOf(resourceBooking) < 0
          )
        )
        return mergedArray.sort(
          (a, b) =>
            new Date(a?.startDate as string).getTime() -
            new Date(b?.startDate as string).getTime()
        )
      })
    )
  }

  fetchBookingByDateRange(
    dates: Date[],
    productIds: any[] | number[],
    limit: number = 25,
    page: number = 1,
    sort: { field: string; order: QuerySortOperator } = {
      field: 'startDate',
      order: 'DESC'
    }
  ) {
    const startDate = dates[0]
    const endDate = dates[1]

    if (!startDate || !endDate || !productIds || productIds?.length === 0) {
      return
    }
    this.store.dispatch(tagActions.fetchMany({}))
    if (!limit && !page && !sort) {
      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            sort: { field: 'startDate', order: 'DESC' },
            search: {
              startDate: {
                [CondOperator.BETWEEN]: [
                  moment(startDate).startOf('day').toISOString(),
                  moment(endDate).endOf('day').toISOString()
                ]
              },
              $or: [
                {
                  linkedProductId: {
                    [CondOperator.IN]: productIds
                  }
                },
                {
                  productId: {
                    [CondOperator.IN]: productIds
                  }
                }
              ]
            },
            join: [
              { field: 'product' },
              { field: 'linkedProduct' },
              // { field: 'product.productGroups' },
              // { field: 'product.parentBookableProducts' }
              { field: 'participations' },
              { field: 'participations.organization' },
              { field: 'saleItems' }, // necessary for reports
              { field: 'saleItems.tags' },
              { field: 'saleItems.sale' },
              { field: 'saleItems.sale.organization' },
              { field: 'saleItems.sale.customer' }
              // { field: 'participations.member' },
              // { field: 'participations.organization' }
              //{ field: 'participations.saleItem.sale' }
              // { field: 'participations.saleItem' },
              // { field: 'participations.saleItem.tags' }
            ]
          })
        })
      )
    } else {
      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            sort,
            limit,
            page,
            search: {
              startDate: {
                [CondOperator.BETWEEN]: [
                  moment(startDate).startOf('day').toISOString(),
                  moment(endDate).endOf('day').toISOString()
                ]
              },
              $or: [
                {
                  linkedProductId: {
                    [CondOperator.IN]: productIds
                  }
                },
                {
                  productId: {
                    [CondOperator.IN]: productIds
                  }
                }
              ]
            },
            join: [
              { field: 'product' },
              { field: 'linkedProduct' },
              // { field: 'product.productGroups' },
              // { field: 'product.parentBookableProducts' }
              { field: 'participations' },
              { field: 'participations.organization' },
              { field: 'saleItems' }, // necessary for reports
              { field: 'saleItems.tags' },
              { field: 'saleItems.sale' },
              { field: 'saleItems.sale.organization' },
              { field: 'saleItems.sale.customer' }
              // { field: 'participations.member' },
              // { field: 'participations.organization' }
              //{ field: 'participations.saleItem.sale' }
              // { field: 'participations.saleItem' },
              // { field: 'participations.saleItem.tags' }
            ]
          })
        })
      )
    }

    this.actions$
      .pipe(
        ofType(bookingActions.upsertMany),
        take(1),
        catchError(() => of(null))
      )
      .subscribe((action) => {
        if (action?.entities?.length > 0) {
          let participations = []
          action.entities.forEach((element) => {
            if (
              element.participations?.length > 0 &&
              !participations.includes(element.participations[0])
            ) {
              participations.push(element.participations[0])
            }
          })
          if (participations?.length > 0) {
            this.store.dispatch(
              participationActions.fetchMany({
                params: RequestQueryBuilder.create({
                  join: [
                    { field: 'saleItem' },
                    { field: 'saleItem.sale' },
                    { field: 'organization' }
                  ],
                  search: { id: { [CondOperator.IN]: participations } }
                })
              })
            )
          }
        }
      })
  }

  fetchBookingItemsByDateRange(dates: Date[], productIds: any[] | number[]) {
    const startDate = dates[0]
    const endDate = dates[1]

    if (!startDate || !endDate) {
      return
    }

    this.store.dispatch(
      bookingItemActions.fetchMany({
        params: RequestQueryBuilder.create({
          sort: { field: 'startDate', order: 'ASC' },
          search: {
            startDate: {
              [CondOperator.BETWEEN]: [
                moment(startDate).startOf('day').toISOString(),
                moment(endDate).endOf('day').toISOString()
              ]
            },
            'saleItem.productId': {
              [CondOperator.IN]: productIds
            }
          },
          join: [
            { field: 'product' },
            { field: 'booking' },
            { field: 'saleItem' }
          ]
        })
      })
    )
  }

  fetchSaleItemsByProductIds(
    productIds: number[],
    startDate: moment.Moment,
    endDate: moment.Moment
  ) {
    if (productIds && productIds.length < 1) {
      return
    }

    this.store.dispatch(
      saleItemActions.fetchMany({
        params: RequestQueryBuilder.create({
          sort: { field: 'id', order: 'DESC' },
          search: {
            productId: {
              [CondOperator.IN]: productIds
            },
            $or: [
              {
                params: {
                  [CondOperator.CONTAINS]: startDate.format('YYYY-MM')
                }
              },
              {
                params: {
                  [CondOperator.CONTAINS]: endDate.format('YYYY-MM')
                }
              }
            ]
          },
          join: [
            { field: 'product' },
            { field: 'sale' },
            { field: 'sale.organization' }
          ]
        })
      })
    )
  }
}
